import React, { useCallback, useEffect, useMemo, useState } from "react";
function useZippopotamus(postcode: string | null) {
    const [location, setLocation] = useState<string | null>(null);
    useEffect(() => {
        if (!postcode) {
            setLocation(null);
            return;
        }
        let cancelled = false;
        fetch(`https://api.zippopotam.us/US/${postcode}`)
            .then((res) => res.ok ? res.json() : null)
            .then((data) => {
                if (cancelled || !data || !data.places || !data.places.length) return;
                const place = data.places[0];
                setLocation(`${place["place name"]}, ${place["state"]} (${postcode})`);
            })
            .catch(() => {
                if (!cancelled) setLocation(null);
            });
        return () => { cancelled = true; };
    }, [postcode]);
    return location;
}
import { AdminSupportChat } from "../messages/AdminSupportChat";
import { Seeker } from "../../types/seeker";
import SeekerMainContent from "../../components/seeker/SeekerMainContent";
import SeekerSidebar from "../../components/seeker/SeekerSidebar";
import SeekerEventsPanel from "../../components/seeker/SeekerEventsPanel";
import GuideDetailModal from "../guides/GuideDetailModal";
import ManualMatchModal from "./ManualMatchModal";
import { getSeekerMatchHistory, getStoredMatchForSeeker, type StoredMatch } from "../../data/matchingService";
import { type ScheduledSession } from "../../data/sessionService";
import { fetchSessionsForSeekerBySeekerId } from "../../components/seeker/SeekerSessionHistory";
import { getSupabaseClient } from "../../lib/supabase";
import { type Event } from "../../data/eventsService";

type Props = {
    seeker: Seeker;
    onClose: () => void;
    onRefresh: () => void;
};

export default function SeekerDetailModal({ seeker, onClose, onRefresh }: Props) {
    const candidateZip = seeker?.locationPostcode ? String(seeker.locationPostcode).replace(/[^0-9]/g, '').slice(0, 5) : null;
    const locationLabel = useZippopotamus(candidateZip);
    const [match, setMatch] = useState<StoredMatch | null>(null);
    const [loadingMatch, setLoadingMatch] = useState<boolean>(false);
    const [matchError, setMatchError] = useState<string | null>(null);
    const [matchHistory, setMatchHistory] = useState<StoredMatch[]>([]);
    const [loadingMatchHistory, setLoadingMatchHistory] = useState<boolean>(false);
    const [matchHistoryError, setMatchHistoryError] = useState<string | null>(null);
    const [showGuideModal, setShowGuideModal] = useState<boolean>(false);
    const [showManualMatchModal, setShowManualMatchModal] = useState<boolean>(false);
    const [sessions, setSessions] = useState<ScheduledSession[]>([]);
    const [loadingSessions, setLoadingSessions] = useState<boolean>(false);
    const [sessionsError, setSessionsError] = useState<string | null>(null);
    const [events, setEvents] = useState<Event[]>([]);
    const [loadingEvents, setLoadingEvents] = useState<boolean>(false);
    const [eventsError, setEventsError] = useState<string | null>(null);
    const [profileOverride, setProfileOverride] = useState<{
        name?: string | null;
        phone?: string | null;
        seekerInitiatedChanges?: number;
        guideInitiatedChanges?: number;
        blocked?: boolean;
        referralSource?: string | null;
        seekerGenders?: string[] | null;
    } | null>(null);
    const [isTogglingBlock, setIsTogglingBlock] = useState(false);
    const [showSupportChat, setShowSupportChat] = useState(false);

    useEffect(() => {
        let cancelled = false;
        if (!seeker.userId) return;

        const supabase = getSupabaseClient();
        supabase
            .from('profiles')
            .select('full_name, phone, seeker_initiated_changes, guide_initiated_changes, blocked')
            .eq('id', seeker.userId)
            .maybeSingle()
            .then(({ data, error }) => {
                if (cancelled) return;
                if (error) {
                    console.warn('Failed to load seeker profile details', error);
                    return;
                }
                if (data) {
                    setProfileOverride((prev) => ({
                        ...(prev ?? {}),
                        name: data.full_name ?? undefined,
                        phone: data.phone ?? undefined,
                        seekerInitiatedChanges: data.seeker_initiated_changes ?? 0,
                        guideInitiatedChanges: data.guide_initiated_changes ?? 0,
                        blocked: Boolean(data.blocked),
                    }));
                }
            });

        return () => {
            cancelled = true;
        };
    }, [seeker.userId]);

    useEffect(() => {
        let cancelled = false;
        if (!seeker.sessionId) return;

        const supabase = getSupabaseClient();
        supabase
            .from('seeker_medical_and_final')
            .select('referral_source')
            .eq('session_id', seeker.sessionId)
            .maybeSingle()
            .then(({ data, error }) => {
                if (cancelled) return;
                if (error) {
                    console.warn('Failed to load seeker referral source', error);
                    return;
                }
                if (data) {
                    setProfileOverride((prev) => ({
                        ...(prev ?? {}),
                        referralSource: data.referral_source ?? null,
                    }));
                }
            });

        return () => {
            cancelled = true;
        };
    }, [seeker.sessionId]);

    useEffect(() => {
        let cancelled = false;
        if (!seeker.sessionId) return;

        const supabase = getSupabaseClient();
        supabase
            .from('seeker_basic_info')
            .select('seeker_genders')
            .eq('session_id', seeker.sessionId)
            .maybeSingle()
            .then(({ data, error }) => {
                if (cancelled) return;
                if (error) {
                    console.warn('Failed to load seeker genders', error);
                    return;
                }
                if (data) {
                    setProfileOverride((prev) => ({
                        ...(prev ?? {}),
                        seekerGenders: data.seeker_genders ?? null,
                    }));
                }
            });

        return () => {
            cancelled = true;
        };
    }, [seeker.sessionId]);

    const displaySeeker = useMemo(() => {
        const overrideName = profileOverride?.name && profileOverride.name.trim().length > 0
            ? profileOverride.name
            : seeker.name;
        const overridePhone = profileOverride?.phone ?? seeker.phone ?? null;
        const overrideReferralSource = profileOverride?.referralSource ?? seeker.referralSource ?? null;
        const overrideSeekerGenders = profileOverride?.seekerGenders ?? seeker.seekerGenders ?? null;
        return {
            ...seeker,
            name: overrideName,
            phone: overridePhone,
            referralSource: overrideReferralSource,
            seekerGenders: overrideSeekerGenders,
        };
    }, [seeker, profileOverride]);

    const activeMatch = useMemo(() => {
        if (!match) return null;
        return match.status === 'active' ? match : null;
    }, [match]);

    const inactiveMatches = useMemo(
        () => matchHistory.filter((historyMatch) => historyMatch.status && historyMatch.status !== 'active'),
        [matchHistory]
    );

    const isSeekerBlocked = Boolean(profileOverride?.blocked);
    const suspendButtonLabel = isSeekerBlocked ? 'Unblock Account' : 'Suspend Account';
    const canToggleBlock = Boolean(seeker.userId);

    const loadMatch = useCallback(async () => {
        setLoadingMatch(true);
        setMatchError(null);
        try {
            const storedMatch = await getStoredMatchForSeeker(seeker.id);
            setMatch(storedMatch);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to load match';
            setMatchError(message);
        } finally {
            setLoadingMatch(false);
        }
    }, [seeker.id]);

    const loadMatchHistory = useCallback(async () => {
        setLoadingMatchHistory(true);
        setMatchHistoryError(null);
        try {
            const history = await getSeekerMatchHistory(seeker.id);
            setMatchHistory(history);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to load match history';
            setMatchHistoryError(message);
            setMatchHistory([]);
        } finally {
            setLoadingMatchHistory(false);
        }
    }, [seeker.id]);

    const loadSessions = useCallback(async () => {
        setLoadingSessions(true);
        setSessionsError(null);
        try {
            if (!seeker?.id) {
                setSessions([]);
                return;
            }

            const seekerSessions = await fetchSessionsForSeekerBySeekerId(seeker.id);
            setSessions(seekerSessions);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to load sessions';
            setSessionsError(message);
        } finally {
            setLoadingSessions(false);
        }
    }, [seeker.id]);

    const loadEvents = useCallback(async () => {
        setLoadingEvents(true);
        setEventsError(null);

        const hasUserId = typeof seeker.userId === 'string' && seeker.userId.length > 0;
        const hasSeekerId = typeof seeker.id === 'string' && seeker.id.length > 0;

        if (!hasUserId && !hasSeekerId) {
            setEvents([]);
            setLoadingEvents(false);
            return;
        }

        try {
            const supabase = getSupabaseClient();
            let query = supabase
                .from('events')
                .select('id, user_id, event_name, metadata, timestamp, created_at')
                .order('timestamp', { ascending: false });

            if (hasUserId && hasSeekerId) {
                query = query.or(`user_id.eq.${seeker.userId},metadata->>seeker_id.eq.${seeker.id}`);
            } else if (hasUserId) {
                query = query.eq('user_id', seeker.userId!);
            } else if (hasSeekerId) {
                query = query.filter('metadata->>seeker_id', 'eq', seeker.id!);
            }

            const { data: eventsData, error: eventsQueryError } = await query;

            if (eventsQueryError) {
                throw new Error(eventsQueryError.message);
            }

            setEvents((eventsData ?? []).filter(Boolean));
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to load events';
            setEventsError(message);
            setEvents([]);
        } finally {
            setLoadingEvents(false);
        }
    }, [seeker.id, seeker.userId]);

    useEffect(() => {
        void loadMatch();
        void loadMatchHistory();
        void loadSessions();
        void loadEvents();
    }, [loadMatch, loadMatchHistory, loadSessions, loadEvents]);

    const handleRefresh = useCallback(() => {
        onRefresh();
        void loadMatch();
        void loadMatchHistory();
        void loadSessions();
        void loadEvents();
    }, [onRefresh, loadMatch, loadMatchHistory, loadSessions, loadEvents]);

    const handleOpenGuideModal = useCallback(() => {
        if (activeMatch?.guide_id) {
            setShowGuideModal(true);
        }
    }, [activeMatch?.guide_id]);

    const handleCloseGuideModal = useCallback(() => {
        setShowGuideModal(false);
    }, []);

    const handleGuideUpdated = useCallback(() => {
        // Refresh the match data when guide is updated
        void loadMatch();
        void loadMatchHistory();
    }, [loadMatch, loadMatchHistory]);

    const handleOpenManualMatch = useCallback(() => {
        setShowManualMatchModal(true);
    }, []);

    const handleCloseManualMatch = useCallback(() => {
        setShowManualMatchModal(false);
    }, []);

    const handleManualMatchSuccess = useCallback(() => {
        // Refresh all data after successful manual match
        void loadMatch();
        void loadMatchHistory();
        void loadSessions();
        void loadEvents();
        onRefresh();
    }, [loadMatch, loadMatchHistory, loadSessions, loadEvents, onRefresh]);

    const handleResetSeekerInitiatedChanges = useCallback(async () => {
        if (!seeker.userId) return;

        try {
            const supabase = getSupabaseClient();
            const { error } = await supabase
                .from('profiles')
                .update({ seeker_initiated_changes: 0 })
                .eq('id', seeker.userId);

            if (error) {
                console.error('Failed to reset seeker_initiated_changes', error);
                alert('Failed to reset seeker initiated changes');
                return;
            }

            // Update local state
            setProfileOverride(prev => prev ? { ...prev, seekerInitiatedChanges: 0 } : null);
            alert('Seeker initiated changes reset successfully');
        } catch (err) {
            console.error('Error resetting seeker_initiated_changes', err);
            alert('Error resetting seeker initiated changes');
        }
    }, [seeker.userId]);

    const handleResetGuideInitiatedChanges = useCallback(async () => {
        if (!seeker.userId) return;

        try {
            const supabase = getSupabaseClient();
            const { error } = await supabase
                .from('profiles')
                .update({ guide_initiated_changes: 0 })
                .eq('id', seeker.userId);

            if (error) {
                console.error('Failed to reset guide_initiated_changes', error);
                alert('Failed to reset guide initiated changes');
                return;
            }

            // Update local state
            setProfileOverride(prev => prev ? { ...prev, guideInitiatedChanges: 0 } : null);
            alert('Guide initiated changes reset successfully');
        } catch (err) {
            console.error('Error resetting guide_initiated_changes', err);
            alert('Error resetting guide initiated changes');
        }
    }, [seeker.userId]);

    const handleToggleSeekerBlock = useCallback(async () => {
        if (!seeker.userId) {
            alert('Seeker account is missing a user ID.');
            return;
        }

        const nextBlockedState = !isSeekerBlocked;
        setIsTogglingBlock(true);

        try {
            const supabase = getSupabaseClient();
            const { data: { session } } = await supabase.auth.getSession();

            if (!session?.access_token) {
                throw new Error('Authentication required');
            }

            const { data, error } = await supabase.functions.invoke('toggle-seeker-block', {
                headers: {
                    Authorization: `Bearer ${session.access_token}`,
                },
                body: {
                    target_user_id: seeker.userId,
                    blocked: nextBlockedState,
                },
            });

            if (error || !data?.success) {
                throw new Error(error?.message ?? 'Failed to update account status');
            }

            setProfileOverride((prev) => ({
                ...(prev ?? {}),
                blocked: nextBlockedState,
            }));

            alert(nextBlockedState ? 'Seeker account suspended.' : 'Seeker account reactivated.');
        } catch (err) {
            console.error('Failed to toggle seeker block status', err);
            alert(err instanceof Error ? err.message : 'Failed to update account status');
        } finally {
            setIsTogglingBlock(false);
        }
    }, [isSeekerBlocked, seeker.userId]);

    const handleExportData = useCallback(() => {
        const basicSeekerInfo = {
            id: seeker.id,
            userId: displaySeeker.userId ?? null,
            name: displaySeeker.name,
            email: displaySeeker.email,
            phone: displaySeeker.phone ?? null,
            status: displaySeeker.status,
            connectedGuides: displaySeeker.connectedGuides,
            lifetimeValue: displaySeeker.lifetimeValue,
            joinedAt: displaySeeker.joinedAt,
        };

        const onboardingInfo = {
            sessionId: displaySeeker.sessionId ?? null,
            seekerOver18: displaySeeker.seekerOver18 ?? null,
            locationPostcode: displaySeeker.locationPostcode ?? null,
            seekerGender: displaySeeker.seekerGender ?? null,
            experienceTypes: displaySeeker.experienceTypes ?? null,
            medicineOptionsComfort: displaySeeker.medicineOptionsComfort ?? null,
            supportTypes: displaySeeker.supportTypes ?? null,
            travelWillingness: displaySeeker.travelWillingness ?? null,
            travelDistance: displaySeeker.travelDistance ?? null,
            focusPreferences: displaySeeker.focusPreferences ?? null,
            preferSpecialistInGroups: displaySeeker.preferSpecialistInGroups ?? null,
            religionAlignmentImportant: displaySeeker.religionAlignmentImportant ?? null,
            seekerBeliefs: displaySeeker.seekerBeliefs ?? null,
            currentlyOnMeds: displaySeeker.currentlyOnMeds ?? null,
            clinicalDiagnoses: displaySeeker.clinicalDiagnoses ?? null,
            budgetBand: displaySeeker.budgetBand ?? null,
            additionalInfo: displaySeeker.additionalInfo ?? null,
            onboardingStartedAt: displaySeeker.onboardingStartedAt ?? null,
            onboardingCompletedAt: displaySeeker.onboardingCompletedAt ?? null,
            onboardingStatus: displaySeeker.onboardingStatus ?? null,
        };

        const matchedGuide = activeMatch
            ? {
                guideId: activeMatch.guide_id,
                matchScore: activeMatch.match_score,
                scoreBreakdown: activeMatch.score_breakdown,
                reasoning: activeMatch.reasoning,
                createdAt: activeMatch.created_at,
                updatedAt: activeMatch.updated_at ?? null,
                status: activeMatch.status ?? null,
                endedAt: activeMatch.ended_at ?? null,
                changeRequest: activeMatch.change_request
                    ? {
                        id: activeMatch.change_request.id,
                        initiator: activeMatch.change_request.initiator,
                        reasonId: activeMatch.change_request.reason_id ?? null,
                        reasonLabel: activeMatch.change_request.reason_label ?? null,
                        details: activeMatch.change_request.details ?? null,
                        status: activeMatch.change_request.status ?? null,
                        createdAt: activeMatch.change_request.created_at ?? null,
                        decidedAt: activeMatch.change_request.decided_at ?? null,
                    }
                    : null,
                guideDetails: activeMatch.guide_details ?? null,
            }
            : null;

        const matchHistoryExport = matchHistory.map((historyMatch) => ({
            id: historyMatch.id,
            guideId: historyMatch.guide_id,
            status: historyMatch.status ?? null,
            createdAt: historyMatch.created_at,
            updatedAt: historyMatch.updated_at ?? null,
            endedAt: historyMatch.ended_at ?? null,
            matchScore: historyMatch.match_score,
            scoreBreakdown: historyMatch.score_breakdown,
            reasoning: historyMatch.reasoning,
            changeRequest: historyMatch.change_request
                ? {
                    id: historyMatch.change_request.id,
                    initiator: historyMatch.change_request.initiator,
                    reasonId: historyMatch.change_request.reason_id ?? null,
                    reasonLabel: historyMatch.change_request.reason_label ?? null,
                    details: historyMatch.change_request.details ?? null,
                    status: historyMatch.change_request.status ?? null,
                    createdAt: historyMatch.change_request.created_at ?? null,
                    decidedAt: historyMatch.change_request.decided_at ?? null,
                }
                : null,
        }));

        const sessionData = sessions.map((session) => ({
            id: session.id,
            guideId: session.guide_id,
            seekerId: session.seeker_id,
            sessionType: session.session_type,
            sessionDate: session.session_date,
            sessionTime: session.session_time,
            durationMinutes: session.duration_minutes,
            sessionStatus: session.session_status,
            notes: session.notes,
            seekerName: session.seeker_name,
            seekerEmail: session.seeker_email,
            seekerPhone: session.seeker_phone,
            createdAt: session.created_at,
            updatedAt: session.updated_at,
        }));

        const exportPayload = {
            generatedAt: new Date().toISOString(),
            seeker: basicSeekerInfo,
            onboarding: onboardingInfo,
            matchedGuide,
            sessions: sessionData,
            matchHistory: matchHistoryExport,
        };

        const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        const safeName = seeker.name
            ? seeker.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
            : 'seeker';
        anchor.download = `seeker-${safeName || seeker.id}-export.json`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        setTimeout(() => URL.revokeObjectURL(url), 0);
    }, [activeMatch, displaySeeker, sessions, matchHistory, seeker.id]);

    return (
        <>
            <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-[#2C2731]/60 px-4 py-8"
                onClick={onClose}
                role="dialog"
                aria-modal="true"
            >
                <div
                    className="relative flex w-full max-w-5xl max-h-[90vh] flex-col overflow-hidden rounded-3xl border border-[#2C2731] bg-[#FFFCEF] shadow-[0_24px_48px_rgba(44,39,49,0.35)]"
                    onClick={(e) => e.stopPropagation()}
                >
                    <header className="flex items-center justify-between border-b border-[#2C2731]/40 px-6 py-5">
                        <h2 className="text-2xl font-semibold text-[#2C2731]">Seeker Details</h2>
                        <button
                            onClick={onClose}
                            aria-label="Close modal"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#2C2731] bg-[#2C2731] text-[#FEF6D6] transition-colors hover:bg-[#3C3450]"
                        >
                            <span className="text-lg leading-none">×</span>
                        </button>
                    </header>

                    <div className="flex flex-1 flex-col overflow-hidden">
                        <div className="flex flex-1 gap-6 overflow-y-auto px-6 py-6">
                            <div className="flex flex-1 flex-col gap-6">
                                <SeekerMainContent
                                    seeker={displaySeeker}
                                    sessions={sessions}
                                    loadingSessions={loadingSessions}
                                    sessionsError={sessionsError}
                                />
                                <SeekerEventsPanel
                                    events={events}
                                    loading={loadingEvents}
                                    error={eventsError}
                                />
                            </div>
                            <SeekerSidebar
                                seeker={displaySeeker}
                                sessions={sessions}
                                onRefresh={handleRefresh}
                                match={match}
                                loadingMatch={loadingMatch}
                                matchError={matchError}
                                onOpenGuide={handleOpenGuideModal}
                                onOpenManualMatch={handleOpenManualMatch}
                                locationLabel={locationLabel}
                                pastMatches={inactiveMatches}
                                loadingPastMatches={loadingMatchHistory}
                                pastMatchesError={matchHistoryError}
                                seekerInitiatedChanges={profileOverride?.seekerInitiatedChanges}
                                guideInitiatedChanges={profileOverride?.guideInitiatedChanges}
                                onResetSeekerInitiatedChanges={handleResetSeekerInitiatedChanges}
                                onResetGuideInitiatedChanges={handleResetGuideInitiatedChanges}
                            />
                        </div>

                        <footer className="flex items-center justify-between gap-3 border-t border-[#2C2731]/40 bg-[#FFFCEF] px-6 py-4">
                            <div className="text-sm font-medium text-[#2C2731]/70">
                                {/*locationLabel ? `Based in ${locationLabel}` : "Location unknown"*/}
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => setShowSupportChat(true)}
                                    disabled={!seeker.userId}
                                    className={`rounded-lg border border-[#2C2731] px-4 py-2 text-sm font-medium text-[#2C2731] transition-colors hover:bg-[#F2EAC3] ${!seeker.userId ? 'opacity-60 cursor-not-allowed' : ''}`}
                                >
                                    Message
                                </button>
                                <button
                                    onClick={handleToggleSeekerBlock}
                                    disabled={!canToggleBlock || isTogglingBlock}
                                    className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                                        isSeekerBlocked
                                            ? 'border-emerald-700 text-emerald-800 hover:bg-emerald-50'
                                            : 'border-[#B9383A] text-[#B9383A] hover:bg-[#FAD9D9]'
                                    } ${(!canToggleBlock || isTogglingBlock) ? 'opacity-60 cursor-not-allowed' : ''}`}
                                >
                                    {isTogglingBlock ? 'Updating…' : suspendButtonLabel}
                                </button>
                                <button
                                    onClick={handleExportData}
                                    className="rounded-lg border border-[#2C2731] px-4 py-2 text-sm font-medium text-[#2C2731] transition-colors hover:bg-[#F2EAC3]"
                                >
                                    Export Data
                                </button>
                                <button
                                    className="rounded-lg border border-[#2C2731] bg-[#2C2731] px-4 py-2 text-sm font-semibold text-[#FEF6D6] transition-colors hover:bg-[#3C3450]"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </footer>
                    </div>

                    {showSupportChat && seeker.userId && (
                        <div className="absolute inset-0 z-10 overflow-hidden rounded-3xl bg-[#FFFCEF]">
                            <AdminSupportChat
                                targetUserId={seeker.userId}
                                userName={displaySeeker.name}
                                userRole="seeker"
                                onBack={() => setShowSupportChat(false)}
                            />
                        </div>
                    )}
                </div>
            </div>

            {showGuideModal && activeMatch?.guide_id && (
                <GuideDetailModal id={activeMatch.guide_id} onClose={handleCloseGuideModal} onUpdated={handleGuideUpdated} />
            )}

            {showManualMatchModal && (
                <ManualMatchModal
                    seekerId={seeker.id}
                    seekerName={displaySeeker.name}
                    onClose={handleCloseManualMatch}
                    onSuccess={handleManualMatchSuccess}
                />
            )}
        </>
    );
}
